import React, { useState } from 'react';
import ChartRenderer from './ChartRenderer';

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Format column key → "Title Case With Spaces" (desktop) */
const formatHeader = (key) =>
  key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

/** Short header labels for mobile table — keeps columns readable at narrow widths */
const MOBILE_ABBREV = {
  reorder_point:    'Reorder',
  invoice_number:   'Invoice #',
  customer_code:    'Customer',
  customer_name:    'Customer',
  item_code:        'Code',
  item_name:        'Item',
  days_overdue:     'Days',
  total_overdue:    'Total',
  overdue_amount:   'Amount',
  credit_limit:     'Limit',
  current_balance:  'Balance',
  sales_rep:        'Rep',
  lead_time_days:   'Lead',
  lost_sales:       'Lost Sales',
  invoice_count:    'Invoices',
};
const formatMobileHeader = (key) => {
  if (MOBILE_ABBREV[key]) return MOBILE_ABBREV[key];
  const full = formatHeader(key);
  // Auto-shorten: if > 10 chars, take first word only
  if (full.length > 10) return full.split(' ')[0];
  return full;
};

/** Columns that should never wrap (codes, numbers, dates) */
const isCompactCol = (col) =>
  /code|invoice|customer|amount|on_hand|reorder|days|date|qty|price|id|no$|num/i.test(col);

/** Short alphanumeric code columns — wider than numbers, narrower than descriptions */
const isCodeCol = (col) =>
  /_code$|_id$|_no$|_num$|_ref$|_key$|^code$|^sku$|^part/i.test(col);

/** Columns where value might be a low-stock warning */
const isLowStock = (col, row) =>
  col === 'on_hand' && row.reorder_point != null && Number(row[col]) < Number(row.reorder_point);

/** Columns where value should be right-aligned (numbers) */
const isNumericCol = (col) =>
  /amount|on_hand|reorder|days|qty|price|count|total|value|inv|stock|balance|share|pct|percent|rate|cost|sales|revenue|units|min|max/i.test(col);

/**
 * Renders markdown-lite content:
 *  - **bold**
 *  - Bullet lines (•, -, *)
 *  - Numbered lines (1. 2.)
 *  - ### Headings
 */
const renderContent = (text) => {
  if (!text) return null;
  return text.split('\n').map((line, i) => {
    const trimmed = line.trimStart();

    // Headings
    if (trimmed.startsWith('### '))
      return <p key={i} className="font-bold text-sm mt-2 mb-0.5" style={{ color: 'var(--text-primary)' }}>{trimmed.slice(4)}</p>;
    if (trimmed.startsWith('## '))
      return <p key={i} className="font-bold text-sm mt-2 mb-0.5" style={{ color: 'var(--text-primary)' }}>{trimmed.slice(3)}</p>;

    // Bold inline
    const boldify = (str) =>
      str.split(/(\*\*[^*]+\*\*)/g).map((part, j) =>
        part.startsWith('**') && part.endsWith('**')
          ? <strong key={j}>{part.slice(2, -2)}</strong>
          : part
      );

    // Numbered list
    const numMatch = trimmed.match(/^(\d+)\.\s+(.*)/);
    if (numMatch)
      return (
        <p key={i} className={`flex gap-2 ${i > 0 ? 'mt-1' : ''}`}>
          <span className="font-semibold text-indigo-500 flex-shrink-0">{numMatch[1]}.</span>
          <span>{boldify(numMatch[2])}</span>
        </p>
      );

    // Bullet list
    if (trimmed.startsWith('• ') || trimmed.startsWith('- ') || trimmed.startsWith('* '))
      return (
        <p key={i} className={`flex gap-2 ${i > 0 ? 'mt-1' : ''}`}>
          {/* SVG bullet — no emoji, matches indigo theme */}
          <svg viewBox="0 0 6 6" width="6" height="6" style={{ flexShrink: 0, marginTop: '6px', fill: '#818cf8' }} aria-hidden="true">
            <circle cx="3" cy="3" r="3" />
          </svg>
          <span>{boldify(trimmed.slice(2))}</span>
        </p>
      );

    // Plain paragraph
    return (
      <p key={i} className={i > 0 && trimmed ? 'mt-1.5' : 'mt-0'}>
        {boldify(line)}
      </p>
    );
  });
};

// ─── TableRenderer ───────────────────────────────────────────────────────────
/**
 * Three-way layout:
 * - Mobile card view (default): compact tile per row, 2-col field grid
 * - Mobile table view: auto-fit table, table-layout:fixed, cols share width equally
 * - Desktop (md+): traditional full-width table (unchanged)
 */
/**
 * TableRenderer: forcedView ('table' | 'card') is controlled by the parent MessageBubble tab bar.
 * No internal toggle — parent owns the view state.
 */
const TableRenderer = ({ table, forcedView = 'table' }) => {
  if (!table?.rows?.length) return null;
  const cols      = Object.keys(table.rows[0]);
  const mobileView = forcedView; // 'table' | 'card', driven by parent

  const cellValue = (col, row) => {
    const low = isLowStock(col, row);
    if (low) return (
      <span style={{ color: '#ef4444', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '4px' }}>
        <i className="fas fa-triangle-exclamation" style={{ fontSize: '10px' }} />
        {row[col]}
      </span>
    );
    return <span style={{ color: 'var(--text-secondary)' }}>{String(row[col])}</span>;
  };

  // Plain string value (no JSX) for compact table cells
  const rawValue = (col, row) => String(row[col] ?? '');

  return (
    <div className="mb-1">

      {/* ── MOBILE CARD VIEW ── */}
      {mobileView === 'card' && (
        <div className="md:hidden space-y-2">
          {table.rows.map((row, i) => {
            const rowIsLow = cols.some(c => isLowStock(c, row));
            const titleCol = cols[0];
            const restCols = cols.slice(1);
            const colPairs = [];
            for (let j = 0; j < restCols.length; j += 2) colPairs.push(restCols.slice(j, j + 2));
            return (
              <div
                key={i}
                className="rounded-xl border overflow-hidden"
                style={{
                  borderColor: 'var(--glass-border)',
                  background:  'var(--bg-glass)',
                  borderLeft:  `3px solid ${rowIsLow ? '#ef4444' : '#6366f1'}`,
                }}
              >
                <div
                  className="px-3 py-2 flex items-center justify-between"
                  style={{ borderBottom: restCols.length ? '1px solid var(--glass-border)' : 'none' }}
                >
                  <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)' }}>
                    {String(row[titleCol] ?? '')}
                  </span>
                  {rowIsLow && (
                    <span style={{
                      fontSize: '9px', fontWeight: 700, textTransform: 'uppercase',
                      letterSpacing: '0.06em', color: '#ef4444',
                      background: 'rgba(239,68,68,0.08)', borderRadius: '99px', padding: '2px 7px',
                    }}>
                      <i className="fas fa-triangle-exclamation" style={{ marginRight: 3 }} />Alert
                    </span>
                  )}
                </div>
                {colPairs.length > 0 && (
                  <div className="px-3 py-2 grid grid-cols-2 gap-x-4 gap-y-2">
                    {colPairs.map(pair =>
                      pair.map(col => (
                        <div key={col}>
                          <p style={{ fontSize: '9px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6366f1', marginBottom: '2px' }}>
                            {formatHeader(col)}
                          </p>
                          <p style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: 500 }}>
                            {cellValue(col, row)}
                          </p>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── MOBILE TABLE VIEW — smart column sizing, # serial, row count ── */}
      {mobileView === 'table' && (() => {
        // Three-tier column weights (no serial # col):
        //   text/description  = 2.5× — names, categories (wraps to 2 lines)
        //   code/ID cols      = 1.5× — ITM-1001, C10025 (nowrap, needs visible width)
        //   numeric cols      = 1.0× — quantities, amounts (smallest)
        const TEXT_W = 2.5;
        const CODE_W = 1.5;
        const NUM_W  = 1.0;
        const colW   = (col) => isNumericCol(col) ? NUM_W : isCodeCol(col) ? CODE_W : TEXT_W;
        const totalW = cols.reduce((s, c) => s + colW(c), 0);
        const colPct = (col) => `${(colW(col) / totalW * 100).toFixed(1)}%`;

        // Dynamic font: fewer columns = larger font (more readable)
        const dynFont = cols.length <= 4 ? '12px' : cols.length <= 6 ? '11px' : '10px';

        return (
          <div className="md:hidden">
            {/* Row count */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
              <span style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: 500 }}>
                {table.rows.length} row{table.rows.length !== 1 ? 's' : ''}
              </span>
              {cols.length > 5 && (
                <span style={{ fontSize: '9px', color: 'var(--text-muted)', opacity: 0.7 }}>
                  Swipe if needed
                </span>
              )}
            </div>
            <div
              className="rounded-xl border"
              style={{
                borderColor: 'var(--glass-border)',
                overflowX: 'auto',
                WebkitOverflowScrolling: 'touch',
                overscrollBehavior: 'contain',
                touchAction: 'pan-x',       // vertical swipes pass through to page scroll on mobile
              }}
            >
              <table style={{ tableLayout: 'fixed', width: '100%', borderCollapse: 'collapse' }}>
                <colgroup>
                  {cols.map(col => <col key={col} style={{ width: colPct(col) }} />)}
                </colgroup>
                {/* Sticky header — stays visible when scrolling many rows */}
                <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                  <tr style={{ background: 'var(--accent-light)' }}>
                    {cols.map((col, ci) => (
                      <th
                        key={col}
                        title={formatHeader(col)}
                        style={{
                          padding: '6px 4px',
                          fontSize: '9px',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: 0,          // no letter-spacing — saves ~15% width per header
                          color: '#6366f1',
                          textAlign: isNumericCol(col) ? 'right' : 'left',
                          borderBottom: '2px solid var(--glass-border)',
                          borderRight: ci < cols.length - 1 ? '1px solid var(--glass-border)' : 'none',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          background: 'var(--accent-light)',
                        }}
                      >
                        {formatMobileHeader(col)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {table.rows.map((row, i) => {
                    const rowIsLow = cols.some(c => isLowStock(c, row));
                    return (
                      <tr
                        key={i}
                        style={{
                          background: i % 2 === 0 ? 'var(--bg-glass)' : 'transparent',
                          borderLeft: `2px solid ${rowIsLow ? '#ef4444' : 'transparent'}`,
                        }}
                      >
                        {cols.map((col, ci) => {
                          const isText = !isNumericCol(col) && !isCodeCol(col);
                          return (
                            <td
                              key={col}
                              title={rawValue(col, row)}
                              style={{
                                padding: '5px 4px',
                                fontSize: dynFont,
                                fontWeight: 500,
                                textAlign: isNumericCol(col) ? 'right' : 'left',
                                verticalAlign: 'top',
                                color: isLowStock(col, row) ? '#ef4444' : 'var(--text-secondary)',
                                borderTop: '1px solid var(--glass-border)',
                                borderRight: ci < cols.length - 1 ? '1px solid var(--glass-border)' : 'none',
                                fontVariantNumeric: isNumericCol(col) ? 'tabular-nums' : 'normal',
                                // Text cols: wrap up to 2 lines — fully visible, no ellipsis
                                // Code/numeric: single line, ellipsis only as last resort
                                ...(isText ? {
                                  overflow: 'hidden',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  whiteSpace: 'normal',
                                  wordBreak: 'break-word',
                                } : {
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  maxWidth: 0,
                                }),
                              }}
                            >
                              {rawValue(col, row)}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {/* ── DESKTOP: traditional table (unchanged) ── */}
      <div className="hidden md:block rounded-xl border overflow-x-auto" style={{ borderColor: 'var(--glass-border)' }}>
        <table style={{ borderCollapse: 'collapse', width: 'max-content', minWidth: '100%' }}>
          <thead>
            <tr style={{ background: 'var(--accent-light)' }}>
              {cols.map((col, ci) => (
                <th
                  key={col}
                  style={{
                    padding: '9px 12px',
                    whiteSpace: 'nowrap',
                    textAlign: isNumericCol(col) ? 'right' : 'left',
                    fontSize: '11px',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    color: '#6366f1',
                    borderBottom: '2px solid var(--glass-border)',
                    borderRight: ci < cols.length - 1 ? '1px solid var(--glass-border)' : 'none',
                  }}
                >
                  {formatHeader(col)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row, i) => (
              <tr key={i} style={{ background: i % 2 === 0 ? 'var(--bg-glass)' : 'transparent' }}>
                {cols.map((col, ci) => (
                  <td
                    key={col}
                    style={{
                      padding: '7px 12px',
                      fontSize: '12px',
                      whiteSpace: isCompactCol(col) ? 'nowrap' : 'normal',
                      maxWidth: isCompactCol(col) ? 'none' : '200px',
                      textAlign: isNumericCol(col) ? 'right' : 'left',
                      verticalAlign: 'top',
                      borderTop: '1px solid var(--glass-border)',
                      borderRight: ci < cols.length - 1 ? '1px solid var(--glass-border)' : 'none',
                    }}
                  >
                    {cellValue(col, row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

    </div>
  );
};

// ─── Format timestamp ────────────────────────────────────────────────────────
const formatTime = (ts) => {
  if (!ts) return '';
  return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

// ─── Export table to CSV (Excel-compatible) ──────────────────────────────────
const exportToCSV = (table) => {
  if (!table?.rows?.length) return;
  const cols = Object.keys(table.rows[0]);
  const header = cols.map(formatHeader).join(',');
  const rows   = table.rows.map(row =>
    cols.map(col => `"${String(row[col] ?? '').replace(/"/g, '""')}"`).join(',')
  ).join('\n');
  // \uFEFF = UTF-8 BOM so Excel opens correctly
  const blob = new Blob(['\uFEFF' + header + '\n' + rows], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `sap-b1-${new Date().toISOString().slice(0,10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// ─── MessageBubble ───────────────────────────────────────────────────────────
const MessageBubble = ({ message, showThinking, theme, onRetry }) => {
  const [thinkOpen,   setThinkOpen]  = useState(false);
  const [toolOpen,    setToolOpen]   = useState(false);
  const [copied,      setCopied]     = useState(false);
  const [saved,       setSaved]      = useState(false);
  const [exported,    setExported]   = useState(false);
  const [shared,      setShared]     = useState(false);

  const isUser    = message.role === 'user';
  const hasTable  = !isUser && message.table?.rows?.length > 0;
  const hasChart  = !isUser && !!message.chart && message.table?.rows?.length > 0;
  const hasData   = hasTable || hasChart;

  // View state: 'chart' (default when chart exists) | 'table' | 'card'
  // useState initializer runs only once per mount — safe to use hasChart here
  const [activeView, setActiveView] = useState(hasChart ? 'chart' : 'table');

  const logoSrc = theme === 'dark'
    ? '/averina-ai-app-icon.png'
    : '/averina-ai-icon.png';

  const handleCopy = () => {
    navigator.clipboard.writeText(message.content ?? '');
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const handleShare = async () => {
    // Build shareable text
    const text   = message.content ?? '';
    const table  = message.table;
    let tableText = '';
    if (table?.rows?.length) {
      const cols  = Object.keys(table.rows[0]);
      const hdr   = cols.map(formatHeader).join(' \t ');
      const rows  = table.rows.map(r => cols.map(c => r[c]).join(' \t ')).join('\n');
      tableText   = `\n\n${hdr}\n${rows}`;
    }
    const shareText = `${text}${tableText}\n\n— Powered by Averina AI\nhttps://sapb1.averinaai.com`;

    if (navigator.share) {
      try {
        await navigator.share({
          title : 'SAP B1 Insight — Averina AI',
          text  : shareText,
          url   : 'https://sapb1.averinaai.com',
        });
      } catch (_) { /* user cancelled */ }
    } else {
      // Desktop fallback — copy formatted text
      await navigator.clipboard.writeText(shareText);
      setShared(true);
      setTimeout(() => setShared(false), 1800);
    }
  };

  const isError = !isUser && message.isError === true;


  return (
    <div className={`flex items-end gap-2 mb-5 msg-enter ${isUser ? 'justify-end' : 'justify-start'}`}>

      {/* AI avatar:
           - Always visible on desktop (md+)
           - Hidden on mobile ONLY when message has a completed table
             → gives table ~40px more width without changing streaming behaviour
             (streaming avatar is a separate block in ChatArea.js — untouched)
      */}
      {!isUser && (
        <div className={`w-8 h-8 rounded-xl overflow-hidden bg-white flex-shrink-0 shadow-md self-start mt-1 ${hasData ? 'hidden md:block' : 'block'}`}>
          <img src={logoSrc} alt="Averina AI" className="w-full h-full object-contain" />
        </div>
      )}

      {/*
        Width strategy:
        - User bubbles: max 72% (stays narrow, speech-bubble feel)
        - AI text-only: max 85% on mobile, 78% on desktop
        - AI with table: nearly full width so table has room to display
      */}
      <div
        className={`flex flex-col gap-1 ${isUser ? 'items-end' : 'items-start'}${!isUser && hasData ? ' msg-table-mobile-full' : ''}`}
        style={{
          maxWidth: isUser
            ? 'min(72%, 420px)'
            : hasData
            ? 'calc(100% - 44px)'   // desktop: full width minus avatar
            : 'min(85%, 560px)',
        }}
      >
        {/* Main bubble */}
        <div
          className={`rounded-2xl ${
            isUser
              ? 'bubble-user text-white rounded-br-sm px-4 py-3'
              : 'bubble-ai rounded-bl-sm px-3 py-3 md:px-4'
          }`}
          style={!isUser ? { color: 'var(--text-primary)' } : {}}
        >
          <div className="text-sm leading-relaxed">

            {/* ── Unified view tab bar ──
                Shows when there is chart or table data.
                Desktop: Chart + Table
                Mobile:  Chart + Table + Card
            */}
            {hasData && (() => {
              const TAB = (view, icon, title, mobileOnly = false) => (
                <button
                  key={view}
                  title={title}
                  onClick={() => setActiveView(view)}
                  className={mobileOnly ? 'md:hidden' : ''}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    width: '28px', height: '28px',
                    borderRadius: '8px', border: '1px solid',
                    borderColor:  activeView === view ? '#6366f1' : 'var(--glass-border)',
                    background:   activeView === view ? 'rgba(99,102,241,0.12)' : 'transparent',
                    color:        activeView === view ? '#6366f1' : 'var(--text-muted)',
                    cursor: 'pointer', transition: 'all 0.15s',
                    flexShrink: 0,
                  }}
                >
                  <i className={`fas fa-${icon}`} style={{ fontSize: '11px' }} />
                </button>
              );
              return (
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '10px' }}>
                  {hasChart && TAB('chart', 'chart-bar',  'Chart')}
                  {hasTable && TAB('table', 'table',      'Table')}
                  {hasTable && TAB('card',  'grip',       'Cards', true)}
                  {message.table?.rows?.length > 0 && (
                    <span style={{ marginLeft: '4px', fontSize: '10px', color: 'var(--text-muted)', fontWeight: 500 }}>
                      {message.table.rows.length} row{message.table.rows.length !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              );
            })()}

            {/* Chart view */}
            {hasChart && activeView === 'chart' && (
              <ChartRenderer chart={message.chart} rows={message.table.rows} />
            )}

            {/* Table / Card view */}
            {hasTable && activeView !== 'chart' && (
              <TableRenderer table={message.table} forcedView={activeView} />
            )}

            {/* Clarification header — shown only for isClarification messages */}
            {message.isClarification && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                marginBottom: '8px', paddingBottom: '8px',
                borderBottom: '1px solid rgba(245,158,11,0.2)',
              }}>
                {/* Question mark SVG — amber */}
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#f59e0b"
                  style={{ width: '14px', height: '14px', flexShrink: 0 }} aria-hidden="true">
                  <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm11.378-3.917c-.89-.777-2.366-.777-3.255 0a.75.75 0 01-.988-1.129c1.454-1.272 3.776-1.272 5.23 0 1.454 1.272 1.454 3.215 0 4.487-.43.376-.91.634-1.452.776V9a.75.75 0 00-1.5 0v2.25a.75.75 0 001.5 0v-.078c.2-.053.388-.14.565-.293.448-.392.447-.99-.1-1.346zm-5.13 8.917a1 1 0 112 0 1 1 0 01-2 0z" clipRule="evenodd" />
                </svg>
                <span style={{ fontSize: '11px', fontWeight: 700, color: '#f59e0b', letterSpacing: '0.03em', textTransform: 'uppercase' }}>
                  Clarification needed
                </span>
              </div>
            )}

            {/* Text summary — error messages show warning SVG icon instead of emoji */}
            {message.content && (
              isError ? (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', color: '#ef4444' }}>
                  {/* Warning triangle SVG */}
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"
                    style={{ width: '15px', height: '15px', flexShrink: 0, marginTop: '2px' }} aria-hidden="true">
                    <path fillRule="evenodd" d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z" clipRule="evenodd" />
                  </svg>
                  <span style={{ fontSize: '13px', lineHeight: '1.5' }}>{message.content}</span>
                </div>
              ) : renderContent(message.content)
            )}


          </div>

          {/* RAG source badge */}
          {!isUser && message.ragDocs?.length > 0 && (
            <div className="mt-3 pt-2 border-t border-indigo-100 flex items-center gap-1.5 text-xs text-emerald-600 font-medium">
              <i className="fas fa-circle-check text-emerald-500" />
              Sourced from SAP B1 documentation
            </div>
          )}

          {/* Thinking accordion */}
          {!isUser && showThinking && message.thinking?.length > 0 && (
            <div className="mt-3">
              <button
                onClick={() => setThinkOpen(!thinkOpen)}
                className="flex items-center gap-1.5 text-xs text-indigo-400 hover:text-indigo-600 font-medium transition-colors"
              >
                <i className={`fas fa-chevron-right text-[10px] transition-transform duration-200 ${thinkOpen ? 'rotate-90' : ''}`} />
                Reasoning steps
              </button>
              {thinkOpen && (
                <div className="mt-2 space-y-1.5 pl-3 border-l-2 border-indigo-100">
                  {message.thinking.map((step, i) => (
                    <div key={i} className="text-xs text-gray-500 flex gap-2">
                      <span className="text-indigo-300 font-bold">{i + 1}.</span>
                      <span>{step}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Tool accordion */}
          {!isUser && message.tool && (
            <div className="mt-2">
              <button
                onClick={() => setToolOpen(!toolOpen)}
                className="flex items-center gap-1.5 text-xs text-violet-400 hover:text-violet-600 font-medium transition-colors"
              >
                <i className={`fas fa-chevron-right text-[10px] transition-transform duration-200 ${toolOpen ? 'rotate-90' : ''}`} />
                Tool: {message.tool.name}
              </button>
              {toolOpen && (
                <div className="mt-2 bg-gray-950 text-emerald-400 rounded-xl p-3 text-xs font-mono overflow-x-auto">
                  <pre className="whitespace-pre-wrap">{JSON.stringify(message.tool, null, 2)}</pre>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Action buttons — AI only */}
        {!isUser && (
          <div className="flex items-center gap-1 pl-1 flex-wrap">
            <button onClick={handleCopy} className="action-btn">
              <i className={`fas ${copied ? 'fa-check text-emerald-500' : 'fa-copy'}`} />
              {copied ? 'Copied!' : 'Copy'}
            </button>
            <button onClick={() => { setSaved(true); setTimeout(() => setSaved(false), 1500); }} className="action-btn">
              <i className={`${saved ? 'fas fa-check text-emerald-500' : 'far fa-bookmark'}`} />
              {saved ? 'Saved!' : 'Save'}
            </button>
            {/* Share */}
            <button onClick={handleShare} className="action-btn" title={navigator.share ? 'Share via apps' : 'Copy shareable text'}>
              <i className={`fas ${shared ? 'fa-check text-emerald-500' : 'fa-share-nodes'}`} />
              {shared ? 'Copied!' : 'Share'}
            </button>
            {/* Export Excel — only when table present */}
            {hasTable && (
              <button
                className="export-btn"
                onClick={() => { exportToCSV(message.table); setExported(true); setTimeout(() => setExported(false), 1500); }}
                title="Export to Excel / CSV"
              >
                {exported ? <><i className="fas fa-check" /> Exported!</> : <><i className="fas fa-file-excel" /> Export Excel</>}
              </button>
            )}
            {message.tool && (
              <button onClick={() => navigator.clipboard.writeText(JSON.stringify(message.tool, null, 2))} className="action-btn">
                <i className="fas fa-chart-simple" /> Analyze
              </button>
            )}
            {/* Retry — only on error messages */}
            {isError && onRetry && (
              <button className="retry-btn" onClick={onRetry}>
                <i className="fas fa-rotate-right" /> Retry
              </button>
            )}
          </div>
        )}

        {/* Timestamp */}
        {message.timestamp && (
          <p className={`msg-timestamp text-[10px] text-gray-400 px-1 flex items-center gap-1 ${isUser ? 'justify-end' : 'justify-start'}`}>
            {isUser && <i className="fas fa-check-double text-blue-400 text-[9px]" />}
            {formatTime(message.timestamp)}
          </p>
        )}
      </div>

      {/* User avatar */}
      {isUser && (
        <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-gray-600 to-gray-800 flex items-center justify-center text-white text-xs flex-shrink-0 shadow self-end">
          <i className="fas fa-user" />
        </div>
      )}
    </div>
  );
};

export default MessageBubble;
